import React, { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiTrash2,
  FiChevronDown,
} from "react-icons/fi";

import { useSelector, useDispatch } from "react-redux";

import {
  PageHeader,
  Button,
  ActionButtonGroup,
  PageSection,
  PageTitle,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  FilterLabel,
  FilterSelect,
  Relative,
  StatusBadge,
  EmailSection,
  EmailFormContainer,
  EmailFormField,
  EmailInput,
  EmailTextArea,
  RecipientsContainer,
  SendToAllContainer,
  SendToAllCheckbox,
  SendToAllLabel,
  PreferenceGrid,
  PreferenceOption,
  PreferenceCheckbox,
  PreferenceDetails,
  PreferenceLabel,
  PreferenceCount,
  EmailFormFooter,
  EmailFormInfo,
  SendEmailButton,
  LoadingSpinner,
  ErrorContainer,
  NoDataContainer,
  NoDataIcon,
  NoDataMessage,
  NoDataSubMessage,
  ModalOverlay,
  ConfirmBox,
  CloseBtn,
  ModalActions,
  Cancel,
  Confirm,
} from "./AdminNewslettersPage.styles";

import AdminContainer from "../../../components/utils/AdminContainer";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../../../components/utils/Loader";
import { toast } from "../../../components/modern-ui/sonner";
import {
  deleteNewsletter,
  getNewsletters,
  sendEmails,
} from "../../../store/slices/newsletters/asyncThunks";
import { selectNewsletters } from "../../../store/slices/newsletters/slice";
import { IoCheckmark, IoClose } from "react-icons/io5";
import { FaTimes } from "react-icons/fa";

const statusOptions = [
  { value: "newProducts", label: "Novi Proizvodi" },
  { value: "discounts", label: "Popusti i Akcije" },
  { value: "news", label: "Vesti i Najave" },
  { value: "newBands", label: "Novi Bendovi" },
];

// Helper funkcija za parsiranje JSON preferences
const parsePreferences = (preferences) => {
  if (!preferences) return {};
  if (typeof preferences === "object") return preferences;
  try {
    return JSON.parse(preferences);
  } catch (error) {
    console.error("Error parsing preferences:", error);
    return {};
  }
};

const AdminNewslettersPage = () => {
  const dispatch = useDispatch();
  const { data, loading, deleteLoading, sendEmailsLoading, pagination, error } =
    useSelector(selectNewsletters);

  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    newsletterId: null,
  });

  const [statusFilter, setStatusFilter] = useState("all");

  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    selectedPreferences: [],
    sendToAll: false,
  });

  // Load orders on component mount with better auth handling
  useEffect(() => {
    const loadOrders = async () => {
      try {
        // Small delay to ensure auth token is loaded
        if (isInitialLoad) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          setIsInitialLoad(false);
        }

        await dispatch(
          getNewsletters({ page: currentPage, limit: 10 })
        ).unwrap();
      } catch (error) {
        console.error("Failed to load orders:", error);

        // If auth error and it's initial load, retry once more with longer delay
        if (
          isInitialLoad &&
          (error.message?.includes("authenticated") ||
            error.message?.includes("401") ||
            error.message?.includes("Authorization"))
        ) {
          console.log("Auth error on initial load - retrying with delay...");
          setTimeout(async () => {
            try {
              await dispatch(
                getNewsletters({ page: currentPage, limit: 10 })
              ).unwrap();
              setIsInitialLoad(false);
            } catch (retryError) {
              console.error("Retry failed:", retryError);
              setIsInitialLoad(false);
            }
          }, 1000);
        } else {
          setIsInitialLoad(false);
        }
      }
    };

    loadOrders();
  }, [dispatch, currentPage]);

  const handleDeleteNewsletter = async () => {
    try {
      await dispatch(deleteNewsletter(deleteConfirm.newsletterId))
        .unwrap()
        .then(() => {
          // Refresh orders list
          toast.success("Uspešno ste obrisali newsletter.");

          dispatch(getNewsletters({ page: currentPage, limit: 10 }));

          setDeleteConfirm({ open: false, newsletterId: null });
        })
        .catch((err) => toast.error(err.message));
    } catch (error) {
      toast.error("Failed to delete order:", error);
    }
  };

  const getStatusText = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.label : status;
  };

  // Funkcije za paginaciju
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Funkcija za otvaranje confirm dijaloga za brisanje
  const openDeleteConfirm = (newsletterId) => {
    setDeleteConfirm({
      open: true,
      newsletterId: newsletterId,
    });
  };

  // Filter orders by status
  const filteredNewsletters =
    data?.filter((newsletter) => {
      if (statusFilter === "all") return true;
      const preferences = parsePreferences(newsletter.preferences);
      return preferences && preferences[statusFilter] === true;
    }) || [];

  const handlePreferenceToggle = (preference) => {
    if (emailForm.sendToAll) return; // Ne menjaj ako je sendToAll aktivno

    setEmailForm((prev) => ({
      ...prev,
      selectedPreferences: prev.selectedPreferences.includes(preference)
        ? prev.selectedPreferences.filter((p) => p !== preference)
        : [...prev.selectedPreferences, preference],
    }));
  };

  const handleSendToAllToggle = () => {
    setEmailForm((prev) => ({
      ...prev,
      sendToAll: !prev.sendToAll,
      selectedPreferences: !prev.sendToAll ? [] : prev.selectedPreferences,
    }));
  };

  // Funkcija za brojanje newsletter-a po preferencijama
  const getPreferenceCount = (preference) => {
    return (
      data?.filter((newsletter) => {
        const preferences = parsePreferences(newsletter.preferences);
        return preferences && preferences[preference] === true;
      }).length || 0
    );
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast.error("Molimo unesite naslov i sadržaj email-a");
      return;
    }

    if (!emailForm.sendToAll && emailForm.selectedPreferences.length === 0) {
      toast.error('Molimo odaberite preferencije ili označite "Pošalji svima"');
      return;
    }

    try {
      // Filtriraj newsletter-e na osnovu preferencija
      let targetNewsletters = [];

      if (emailForm.sendToAll) {
        targetNewsletters = data;
      } else {
        targetNewsletters = data.filter((newsletter) => {
          const preferences = parsePreferences(newsletter.preferences);
          if (!preferences) return false;
          return emailForm.selectedPreferences.some(
            (preference) => preferences[preference] === true
          );
        });
      }

      if (targetNewsletters.length === 0) {
        toast.error(
          "Nema newsletter-a koji odgovaraju odabranim preferencijama"
        );
        return;
      }

      const emailIds = targetNewsletters.map((newsletter) => newsletter.id);

      const result = await dispatch(
        sendEmails({
          emailIds,
          subject: emailForm.subject,
          htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #333; text-align: center;">${
              emailForm.subject
            }</h2>
            <div style="line-height: 1.6; color: #666;">
              ${emailForm.message.replace(/\n/g, "<br>")}
            </div>
            <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;">
            <p style="font-size: 12px; color: #999; text-align: center;">
              MRC Marketing Team<br>
              marketing@mrc.rs
            </p>
          </div>
        `,
        })
      ).unwrap();

      toast.success(
        `Email uspešno poslat! Poslano: ${result.sent}, Neuspešno: ${result.failed}`
      );

      // Resetuj form
      setEmailForm({
        subject: "",
        message: "",
        selectedPreferences: [],
        sendToAll: false,
      });
    } catch (error) {
      console.error("Error sending emails:", error);
      toast.error(
        "Greška pri slanju email-a: " + (error.message || "Neočekivana greška")
      );
    }
  };

  if ((loading && !filteredNewsletters?.length) || isInitialLoad) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Newsletters-ima</PageTitle>
          </PageHeader>

          <Loader title={"newsletters-a"} />
        </PageSection>
      </AdminContainer>
    );
  }

  if (error && !isInitialLoad) {
    return (
      <AdminContainer>
        <PageSection>
          <PageHeader>
            <PageTitle>Upravljanje Newsletters-im</PageTitle>
          </PageHeader>
          <ErrorContainer>
            <p>Greška pri učitavanju newsletters-a: {error}</p>
            <Button
              $variant="primary"
              onClick={() => {
                dispatch(getNewsletters({ page: currentPage, limit: 10 }));
              }}
            >
              Pokušaj ponovo
            </Button>
          </ErrorContainer>
        </PageSection>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <PageSection>
        <PageHeader>
          <PageTitle>Upravljanje Newsletters-ima</PageTitle>
          <Relative>
            <FilterSelect
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Sve reference</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown
              color="#ff6b35"
              size={20}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
          </Relative>
        </PageHeader>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <TableHeader>ID</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>Novi Proizvodi</TableHeader>
                <TableHeader>Popusti i Akcije</TableHeader>
                <TableHeader>Vesti i Najave</TableHeader>
                <TableHeader>Novi Bendovi</TableHeader>
                <TableHeader>Akcije</TableHeader>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRow>
                  <NoDataContainer colSpan={7}>
                    <Loader title={"newsletters-a"} />
                  </NoDataContainer>
                </TableRow>
              ) : filteredNewsletters?.length === 0 ? (
                <TableRow>
                  <NoDataContainer colSpan={7}>
                    <NoDataIcon>
                      <FiFileText size={32} color="#666" />
                    </NoDataIcon>
                    <NoDataMessage>
                      {statusFilter === "all"
                        ? "Trenutno nema newsletters-a"
                        : `Nema newsletter-a sa filterom "${getStatusText(
                            statusFilter
                          )}"`}
                    </NoDataMessage>
                    <NoDataSubMessage>
                      {statusFilter === "all"
                        ? "Novi newsletter-i će se prikazati ovde"
                        : "Pokušajte sa drugim filterom"}
                    </NoDataSubMessage>
                  </NoDataContainer>
                </TableRow>
              ) : (
                filteredNewsletters?.map((newsletter) => {
                  const preferences = parsePreferences(newsletter.preferences);
                  return (
                    <TableRow key={newsletter.id}>
                      <TableCell>{newsletter.id}</TableCell>
                      <TableCell>{newsletter.email || "N/A"}</TableCell>
                      <TableCell>
                        {preferences.newProducts ? (
                          <StatusBadge $status="delivered">
                            <IoCheckmark size={16} />
                          </StatusBadge>
                        ) : (
                          <StatusBadge $status="canceled">
                            <IoClose size={16} />
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        {preferences.discounts ? (
                          <StatusBadge $status="delivered">
                            <IoCheckmark size={16} />
                          </StatusBadge>
                        ) : (
                          <StatusBadge $status="canceled">
                            <IoClose size={16} />
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        {preferences.news ? (
                          <StatusBadge $status="delivered">
                            <IoCheckmark size={16} />
                          </StatusBadge>
                        ) : (
                          <StatusBadge $status="canceled">
                            <IoClose size={16} />
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        {preferences.newBands ? (
                          <StatusBadge $status="delivered">
                            <IoCheckmark size={16} />
                          </StatusBadge>
                        ) : (
                          <StatusBadge $status="canceled">
                            <IoClose size={16} />
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        <ActionButtonGroup>
                          <Button
                            $variant="danger"
                            onClick={() => openDeleteConfirm(newsletter.id)}
                            disabled={deleteLoading}
                            title="Obriši newsletter"
                          >
                            <FiTrash2 size={16} />
                          </Button>
                        </ActionButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>

          <PaginationContainer>
            <PaginationInfo>
              Prikazuje se {filteredNewsletters?.length} od{" "}
              {pagination?.totalItems} newsletters-a
              {statusFilter !== "all" &&
                ` (filtrirano po preferencama: ${getStatusText(statusFilter)})`}
            </PaginationInfo>

            <div>
              <PaginationButton
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <FiChevronLeft />
              </PaginationButton>

              {Array.from(
                { length: pagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationButton
                  key={page}
                  onClick={() => goToPage(page)}
                  $active={currentPage === page}
                  disabled={loading}
                >
                  {page}
                </PaginationButton>
              ))}

              <PaginationButton
                onClick={goToNextPage}
                disabled={currentPage === pagination.totalPages || loading}
              >
                <FiChevronRight />
              </PaginationButton>
            </div>
          </PaginationContainer>
        </TableContainer>
      </PageSection>

      <EmailSection>
        <PageHeader>
          <PageTitle>Pošalji Email</PageTitle>
        </PageHeader>

        <EmailFormContainer>
          {/* Email Subject */}
          <EmailFormField>
            <FilterLabel htmlFor="email-subject">Naslov email-a:</FilterLabel>
            <EmailInput
              id="email-subject"
              type="text"
              value={emailForm.subject}
              onChange={(e) =>
                setEmailForm((prev) => ({ ...prev, subject: e.target.value }))
              }
              placeholder="Unesite naslov email-a..."
              disabled={sendEmailsLoading}
            />
          </EmailFormField>

          {/* Recipients Selection */}
          <RecipientsContainer>
            <FilterLabel>Primaoci:</FilterLabel>

            {/* Send to All Checkbox */}
            <SendToAllContainer $active={emailForm.sendToAll}>
              <SendToAllCheckbox
                type="checkbox"
                id="send-to-all"
                checked={emailForm.sendToAll}
                onChange={handleSendToAllToggle}
                disabled={sendEmailsLoading}
              />
              <SendToAllLabel htmlFor="send-to-all">
                Pošalji svim pretplatnicima ({data?.length || 0} ukupno)
              </SendToAllLabel>
            </SendToAllContainer>

            {/* Preference Checkboxes */}
            <PreferenceGrid $disabled={emailForm.sendToAll}>
              {statusOptions.map((option) => {
                const count = getPreferenceCount(option.value);
                const isSelected = emailForm.selectedPreferences.includes(
                  option.value
                );

                return (
                  <PreferenceOption
                    key={option.value}
                    $selected={isSelected}
                    $disabled={sendEmailsLoading}
                    onClick={() =>
                      !sendEmailsLoading && handlePreferenceToggle(option.value)
                    }
                  >
                    <PreferenceCheckbox
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handlePreferenceToggle(option.value)}
                      disabled={sendEmailsLoading || emailForm.sendToAll}
                    />
                    <PreferenceDetails>
                      <PreferenceLabel>{option.label}</PreferenceLabel>
                      <PreferenceCount>
                        {count} pretplatnik
                        {count === 1 ? "" : count < 5 ? "a" : "a"}
                      </PreferenceCount>
                    </PreferenceDetails>
                  </PreferenceOption>
                );
              })}
            </PreferenceGrid>
          </RecipientsContainer>

          {/* Email Content */}
          <EmailFormField>
            <FilterLabel htmlFor="email-content">Sadržaj email-a:</FilterLabel>
            <EmailTextArea
              id="email-content"
              value={emailForm.message}
              onChange={(e) =>
                setEmailForm((prev) => ({ ...prev, message: e.target.value }))
              }
              placeholder="Unesite sadržaj email-a..."
              rows={8}
              disabled={sendEmailsLoading}
            />
          </EmailFormField>

          {/* Send Button and Info */}
          <EmailFormFooter>
            <EmailFormInfo>
              {emailForm.sendToAll
                ? `Email će biti poslat svim pretplatnicima (${
                    data?.length || 0
                  })`
                : emailForm.selectedPreferences.length > 0
                ? `Email će biti poslat za: ${emailForm.selectedPreferences
                    .map(
                      (pref) =>
                        statusOptions.find((opt) => opt.value === pref)?.label
                    )
                    .join(", ")}`
                : 'Odaberite preferencije ili označite "Pošalji svima"'}
            </EmailFormInfo>

            <SendEmailButton
              $variant="primary"
              onClick={handleSendEmail}
              disabled={
                sendEmailsLoading ||
                !emailForm.subject.trim() ||
                !emailForm.message.trim()
              }
            >
              {sendEmailsLoading ? (
                <>
                  <LoadingSpinner />
                  Šaljem...
                </>
              ) : (
                "Pošalji Email"
              )}
            </SendEmailButton>
          </EmailFormFooter>
        </EmailFormContainer>
      </EmailSection>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm.open && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirm({ open: false, orderId: null })}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => setDeleteConfirm({ open: false, orderId: null })}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li ste sigurni da želite da obrišete newsletter?</p>
              <ModalActions>
                <Cancel
                  onClick={() =>
                    setDeleteConfirm({
                      open: false,
                      newsletterId: null,
                    })
                  }
                  disabled={deleteLoading}
                >
                  Otkaži
                </Cancel>

                <Confirm
                  onClick={handleDeleteNewsletter}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Brišem..." : "Obriši"}
                </Confirm>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default AdminNewslettersPage;
